import { useEffect, useState } from 'react'
import Image from '@tiptap/extension-image'
import { NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import AttachmentService from '../../../../../services/attachment.service'
import {
    fetchAttachmentObjectUrl,
    fetchPublicAttachmentObjectUrl,
} from '../../../../../helpers/attachmentImage'

const AttachmentImageView = ({ node, selected }: NodeViewProps) => {
    const { src, alt, title, attachmentId, pendingId } = node.attrs
    const [displaySrc, setDisplaySrc] = useState(src || '')

    useEffect(() => {
        let disposed = false
        let objectUrl: string | null = null

        if (!attachmentId) {
            setDisplaySrc(src || '')
            return () => undefined
        }

        const loadAttachment = async () => {
            try {
                const data = await AttachmentService.publicUrl(attachmentId)
                const publicUrl = data?.file_url || data?.url || data?.data?.file_url || data?.data?.url

                if (publicUrl) {
                    objectUrl = await fetchPublicAttachmentObjectUrl(publicUrl, alt || title || '')
                }

                if (!objectUrl && src) {
                    objectUrl = await fetchAttachmentObjectUrl(src)
                }

                if (!disposed && objectUrl) {
                    setDisplaySrc(objectUrl)
                }
            } catch (error) {
                console.error('Failed to load inline task image:', error)
            }
        }

        void loadAttachment()

        return () => {
            disposed = true
            if (objectUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [alt, attachmentId, src, title])

    return (
        <NodeViewWrapper
            className={`tiptap-attachment-image ${pendingId ? 'is-uploading' : ''}`}
            data-attachment-id={attachmentId || undefined}
        >
            <img
                src={displaySrc}
                alt={alt || ''}
                title={title || undefined}
                className={selected ? 'ProseMirror-selectednode' : undefined}
                draggable={false}
            />
        </NodeViewWrapper>
    )
}

export const AttachmentImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            attachmentId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-attachment-id'),
                renderHTML: (attributes) => attributes.attachmentId
                    ? { 'data-attachment-id': String(attributes.attachmentId) }
                    : {},
            },
            pendingId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-pending-image-id'),
                renderHTML: (attributes) => attributes.pendingId
                    ? { 'data-pending-image-id': String(attributes.pendingId) }
                    : {},
            },
        }
    },

    addNodeView() {
        return ReactNodeViewRenderer(AttachmentImageView)
    },
})
